import type { AnnotatedJsonSchema } from '@web-config-app/core';

/**
 * Predicate function to determine if a schema node matches certain criteria
 */
export type SchemaPathPredicate = (schema: AnnotatedJsonSchema) => boolean;

/**
 * Recursively traverses a schema to find all paths where the predicate returns true.
 * Unlike computeSchema, this traverses ALL combinator branches (oneOf, anyOf, allOf)
 * to find all possible matching paths.
 *
 * @param schema - The JSON Schema to traverse
 * @param predicate - Function to determine if a schema node matches
 * @param currentPath - Internal parameter for tracking the current schema path
 * @param paths - Internal parameter for accumulating matching paths
 * @returns A Set of schema paths (in dot notation) where the predicate returned true
 *
 * @example
 * ```typescript
 * // Find all paths with x-entity-generate annotation
 * const generatedIdPaths = findSchemaPathsWhere(
 *   schema,
 *   (s) => s['x-entity-generate']?.type === 'id'
 * );
 *
 * // Find all image asset references
 * const imageAssetPaths = findSchemaPathsWhere(
 *   schema,
 *   (s) => s['x-entity-asset']?.type === 'image' && !s.format
 * );
 * ```
 */
export const findSchemaPathsWhere = (
  schema: AnnotatedJsonSchema,
  predicate: SchemaPathPredicate,
  currentPath: string = '',
  paths: Set<string> = new Set(),
): Set<string> => {
  if (!schema || typeof schema !== 'object') {
    return paths;
  }

  // Check if current schema node matches the predicate
  if (currentPath && predicate(schema)) {
    paths.add(currentPath);
  }

  // Handle object properties (type may be implicit if properties exist)
  if (schema.properties) {
    Object.entries(schema.properties).forEach(([propName, propSchema]) => {
      const propPath = currentPath
        ? `${currentPath}.properties.${propName}`
        : `properties.${propName}`;
      findSchemaPathsWhere(
        propSchema as AnnotatedJsonSchema,
        predicate,
        propPath,
        paths,
      );
    });
  }

  // Handle arrays
  if (schema.type === 'array' && schema.items) {
    const itemsPath = currentPath ? `${currentPath}.items` : 'items';
    findSchemaPathsWhere(
      schema.items as AnnotatedJsonSchema,
      predicate,
      itemsPath,
      paths,
    );
  }

  // Handle combinators - traverse ALL branches
  ['oneOf', 'anyOf', 'allOf'].forEach((combinator) => {
    const combinatorSchemas = (schema as any)[combinator];
    if (Array.isArray(combinatorSchemas)) {
      combinatorSchemas.forEach((combinatorSchema: AnnotatedJsonSchema) => {
        // For combinators, we continue from the current path (not adding combinator to path)
        // because we want to collect all possible properties at this level
        findSchemaPathsWhere(combinatorSchema, predicate, currentPath, paths);
      });
    }
  });

  return paths;
};
